/**
 * Charts still drawing, for the print route (MAP-2).
 *
 * `usePrintReady` used to assume a chart had painted two frames after its
 * chunk loaded. A map cannot: its boundaries arrive by a request of their
 * own, after the canvas mounts. Each map registers here while it loads and
 * resolves once it has drawn — or failed, because a page that never signals
 * ready is a failed PDF, and a map that could not load is better printed with
 * its note than not printed at all.
 *
 * The print page says how many maps it has, and the wait is for that many to
 * have *started* as well as finished: a map that has not mounted yet has not
 * registered, and an empty list is not proof that nothing is drawing.
 *
 * Deliberately tiny and dependency-free: the print hook imports it on every
 * page, and it must not pull a chart library in with it.
 */

const pending = new Set<Promise<void>>();
let started = 0;

/** How long a page waits for maps that never mount before printing anyway. */
const MOUNT_DEADLINE_MS = 20_000;

/** Mark one drawing in progress; call the returned function when it is done. */
export function beginDrawing(): () => void {
  started += 1;
  let finish: () => void = () => undefined;
  const drawing = new Promise<void>((resolve) => {
    finish = resolve;
  });
  pending.add(drawing);
  void drawing.then(() => pending.delete(drawing));
  return finish;
}

/** Resolves once `expected` drawings have started and every one has finished. */
export async function drawingsSettled(expected = 0): Promise<void> {
  const deadline = Date.now() + MOUNT_DEADLINE_MS;
  while (started < expected && Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
  while (pending.size) {
    await Promise.all([...pending]);
  }
}
